import { useState, useActionState } from 'react';

import { useToast } from '../../../hooks/useToast';
import { DownloadSchema } from '../../../modules/reports/api/reports.schema';
import { selectableApplications } from '../FieldSets/ApplicationsSelector/ApplicationsSelector';
import { Customer } from '../../../modules/customers/api/customer.schema';
import { Contract } from '../../../modules/contracts/api/contract.schema';

const initialFormState = {
    errors: null,
}

interface FormData {
    applications: number[];
    last30days: boolean;
    start_date: string;
    end_date: string;
    contracts: Array<Pick<Contract, 'id' | 'name'>>;
    customers: Array<Pick<Customer, 'id' | 'name'>>;
}

type FormState = {
    errors: {
        [key: string]: string[];
    } | null;
};

export default function useDownloadReportForm() {
    const [formData, setFormData] = useState<FormData>({
        applications: selectableApplications.map(({ value }) => (
            value
        )),
        last30days: true,
        start_date: '',
        end_date: '',
        contracts: [],
        customers: [],
    });

    const { addToast } = useToast();

    const [formState, handleSubmitForm, formPending] = useActionState<FormState>(async () => {
        const validatedFields = DownloadSchema.safeParse(formData);

        if (!validatedFields.success) {
            return {
                success: false,
                errors: validatedFields.error.flatten().fieldErrors,
                message: "Validation failed"
            };
        }

        const params: string[] = [];

        const formDataContractIds = formData.contracts.map(({ id }) => id);
        if (formDataContractIds.length) {
            params.push(`contracts=${formDataContractIds.join(',')}`);
        }

        if (formData.applications.length) {
            params.push(`applications=${formData.applications.join(',')}`);
        }

        if (formData.last30days === false) {
            if (formData.start_date) {
                params.push(`start_date=${formData.start_date}`);
            }

            if (formData.end_date) {
                params.push(`end_date=${formData.end_date}`);
            }
        }

        const finalParams = params.length ? `?${params.join('&')}` : '';

        try {
            // Fetch the report as a blob to enable loading state
            const response = await fetch(`/api/reports/download${finalParams}`);

            if (!response.ok) {
                throw new Error('Failed to download report');
            }

            const url = URL.createObjectURL(await response.blob());

            // Trigger download
            const a = document.createElement('a');
            a.href = url;
            a.download = 'login-report.csv';
            a.click();

            // Clean up the object URL
            URL.revokeObjectURL(url);

            return {
                errors: null,
            };
        } catch (error) { // eslint-disable-line @typescript-eslint/no-unused-vars
            addToast('Error creating report. Please contact support', 'error');
            return {
                errors: null,
            };
        }
    }, initialFormState);

    return {
        formData,
        setFormData,

        formState,
        formPending,
        handleSubmitForm,
    }
};
